import { useEffect, useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { ProductPicker } from "../../components/ProductPicker";
import { firstProblem, minLen, isPositiveAmount } from "../../components/validators";
import type { EngineOverviewDto, EngineRowDto, PriceListAction, ProductCatalogItem } from "../../ipc/types";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones de ciclo de vida disponibles según el estado mostrado. */
function actionsFor(statusLabel: string): PriceListAction[] {
  if (statusLabel === "Borrador") return ["publish", "archive"];
  if (statusLabel === "Publicada") return ["archive"];
  return [];
}
const ACTION_LABEL: Record<PriceListAction, string> = { publish: "Publicar", archive: "Archivar" };

function NewListForm({ onCreate, onCancel, busy }: { onCreate: (v: { name: string }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [name, setName] = useState("");
  const hint = firstProblem([{ ok: minLen(name, 2), msg: "El nombre debe tener al menos 2 caracteres" }]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nueva lista de precio" subtitle="Se crea en Borrador; agrégale precios y luego publícala" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field pd-form__full"><span className="pd-field__label">Nombre</span>
            <input className="pd-input" value={name} placeholder="Ej: Lista general Mercado Libre" aria-label="Nombre" onChange={(e) => setName(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ name: name.trim() })}>
            {busy ? "Creando…" : "Crear lista"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ListRow({ row, catalog, busy, onAddPrice, onAction }: {
  row: EngineRowDto; catalog: readonly ProductCatalogItem[]; busy: boolean;
  onAddPrice: (id: string, productRef: string, unitPriceMinor: number) => void;
  onAction: (id: string, action: PriceListAction) => void;
}): JSX.Element {
  const [ref, setRef] = useState("");
  const [price, setPrice] = useState("");
  const priceNum = Number(price);
  const isDraft = row.status.label === "Borrador";
  const canAdd = !busy && minLen(ref, 2) && isPositiveAmount(price);
  return (
    <tr>
      <td><div className="strong">{row.primary}</div></td>
      <td className="muted">{row.secondary}</td>
      <td><Badge tone={row.status.tone} dot>{row.status.label}</Badge></td>
      <td className="strong">{row.amount}</td>
      <td>
        <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {isDraft ? (
            <>
              <span style={{ width: 120, display: "inline-block" }}><ProductPicker catalog={catalog} value={ref} onChange={setRef} ariaLabel={`Producto ${row.primary}`} placeholder="Producto" /></span>
              <input className="pd-input" value={price} type="number" min="0" placeholder="Precio" aria-label={`Precio ${row.primary}`} style={{ width: 84 }} onChange={(e) => setPrice(e.target.value)} />
              <Button variant="secondary" size="sm" disabled={!canAdd} onClick={() => { onAddPrice(row.id, ref.trim(), Math.round(priceNum)); setRef(""); setPrice(""); }}>Agregar</Button>
            </>
          ) : null}
          {actionsFor(row.status.label).map((a) => (
            <Button key={a} variant={a === "publish" ? "secondary" : "ghost"} size="sm" disabled={busy} onClick={() => onAction(row.id, a)}>{ACTION_LABEL[a]}</Button>
          ))}
          {!isDraft && actionsFor(row.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
        </div>
      </td>
    </tr>
  );
}

export function PricingScreen(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("pricing"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<readonly ProductCatalogItem[]>([]);

  useEffect(() => {
    let active = true;
    client.getProductCatalog().then((res) => { if (active && res.ok) setCatalog(res.value.items); });
    return () => { active = false; };
  }, [client]);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { name: string }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createPriceList(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Lista "${v.name}" creada.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const addPrice = async (id: string, productRef: string, unitPriceMinor: number): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.addPriceToList({ id, productRef, unitPriceMinor });
    setBusy(false);
    if (res.ok && res.value.ok) { setMsg(`Precio agregado a “${productRef}”.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo agregar" : res.error.message);
  };
  const action = async (id: string, act: PriceListAction): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.setPriceListStatus(id, act);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="pricing" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Precios</h1>
              <p className="pagehead__sub">Listas de precio por producto. Crea, define precios y publica; se guardan en tu equipo.</p>
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics }} />
            <Button size="sm" icon="pricing" onClick={() => setShowForm((s) => !s)}>Nueva lista</Button>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewListForm onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Listas de precio" subtitle={`${data.rows.length} listas`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="pricing" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes listas de precio</div><div className="faint" style={{ fontSize: 13 }}>Crea la primera con “Nueva lista”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Lista</th><th>Detalle</th><th>Estado</th><th>SKU</th><th>Precios / acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => <ListRow key={r.id} row={r} catalog={catalog} busy={busy} onAddPrice={addPrice} onAction={action} />)}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
